/**
 * Screening Room — filter model, URL serialization, and label helpers.
 *
 * The filter object is the single source of truth sent to `searchTitles`.
 * It is deliberately API-neutral: the adapter (written against the EXISTING
 * catalog endpoints) translates it to TMDB / Mongo queries. Never filter only
 * the current page on the client.
 */

export const DEFAULT_PAGE_SIZE = 30; // 5 rows × 6 columns. Must stay a multiple of 6.

/** Content types. An empty `types` array means "Movies + TV" (one combined grid). */
export const CONTENT_TYPES = [
  { id: 'movie', label: 'Movies' },
  { id: 'tv', label: 'TV Series' },
];

/** Circle filters — based on friends' own activity (definitions in DECISIONS.md). */
// Two friend filters (DECISIONS #19). Used together they ADD results (a title
// matches if either is true). "Trending in my circle" was retired.
export const CIRCLE_FILTERS = [
  { id: 'circleRatedHigh', label: 'Loved by Friends', chip: 'Loved by Friends', hint: 'A friend rated it 8+ or “Worth it”' },
  { id: 'circleWatching', label: 'Friends Watching Now', chip: 'Friends Watching Now', hint: 'A friend is watching it right now' },
];

/** API-neutral sort keys. The adapter maps these per media type
 *  (e.g. `newest` → movie: primary_release_date.desc, tv: first_air_date.desc). */
export const SORT_OPTIONS = [
  { id: 'popular', label: 'Popular' },
  { id: 'rating', label: 'Highest Rated' },
  { id: 'newest', label: 'Newest' },
  { id: 'oldest', label: 'Oldest' },
  { id: 'title', label: 'Title A–Z' },
];

/** Advanced: runtime buckets (minutes; for TV this is episode length). */
export const RUNTIME_OPTIONS = [
  { id: 'any', label: 'Any length', min: null, max: null },
  { id: 'short', label: 'Under 90 min', min: null, max: 89 },
  { id: 'medium', label: '90–120 min', min: 90, max: 120 },
  { id: 'long', label: 'Over 2 hours', min: 121, max: null },
];

/** Advanced: age-rating ceiling (US). Each step pairs the movie and TV scales. */
export const AGE_OPTIONS = [
  { id: 'any', label: 'Any age rating', movie: null, tv: null },
  { id: 'G', label: 'Up to G / TV-Y7', movie: 'G', tv: 'TV-Y7' },
  { id: 'PG', label: 'Up to PG / TV-PG', movie: 'PG', tv: 'TV-PG' },
  { id: 'PG-13', label: 'Up to PG-13 / TV-14', movie: 'PG-13', tv: 'TV-14' },
  { id: 'R', label: 'Up to R / TV-MA', movie: 'R', tv: 'TV-MA' },
];

/** Advanced: default original-language list (ISO 639-1). Override with the `languageOptions` prop. */
export const DEFAULT_LANGUAGES = [
  ['en', 'English'], ['es', 'Spanish'], ['fr', 'French'], ['de', 'German'], ['it', 'Italian'],
  ['ja', 'Japanese'], ['ko', 'Korean'], ['zh', 'Chinese'], ['hi', 'Hindi'], ['pt', 'Portuguese'],
  ['sv', 'Swedish'], ['da', 'Danish'], ['no', 'Norwegian'], ['tr', 'Turkish'],
].map(([id, name]) => ({ id, name }));

export const RATING_MIN = 0;
export const RATING_MAX = 10;
export const RATING_STEP = 1; // whole numbers only — 1+, 2+, 3+ ... 10, no half-points

export const DEFAULT_FILTERS = Object.freeze({
  q: '',
  types: [], // [] = movie + tv
  genres: [], // genre ids from the app's existing genre list
  providers: [], // TMDB provider ids
  yearFrom: null,
  yearTo: null,
  ratingMin: RATING_MIN,
  ratingMax: RATING_MAX,
  unwatched: false, // hide titles the member has watched
  hideWatching: false, // hide titles the member is currently watching
  circleRatedHigh: false,
  circleTrending: false,
  circleWatching: false,
  // Advanced
  minVotes: 25, // minimum number of ratings — visible default, not hidden; clear to "Any" (null) for no floor
  runtime: 'any', // RUNTIME_OPTIONS id
  language: '', // ISO 639-1 original language, '' = any
  ageMax: 'any', // AGE_OPTIONS id
  releasedOnly: false, // hide titles not yet released
  atHome: false, // only titles streaming now (subscription / free / ads)
  inTheaters: false, // only movies currently in theaters (movies only)
  sort: 'newest',
  page: 1,
});

export const VIEWS = ['grid', 'list', 'compact'];

/* ------------------------------------------------------------------ URL -- */

const LIST_KEYS = { types: 'type', genres: 'genre', providers: 'svc' };
// minVotes is deliberately NOT in SCALAR_KEYS: its default (25) is a
// visible, non-hidden value, so "untouched default" and "explicitly
// cleared to Any" must stay distinguishable on the wire (unlike the other
// scalars below, where default IS the unset/"Any" state already).
const SCALAR_KEYS = {
  q: 'q',
  yearFrom: 'from',
  yearTo: 'to',
  ratingMin: 'rmin',
  ratingMax: 'rmax',
  runtime: 'len',
  language: 'lang',
  ageMax: 'age',
  sort: 'sort',
  page: 'page',
};
// "On my services" is deliberately NOT a filter key here — it's the same
// single global preference Discover's rails use (`useAvailabilityFilter`,
// backed by the member's profile), not a per-search choice, so it's never
// part of this model, the URL, or a saved search's snapshot. The search
// request still sends it (see ScreeningRoomBrowse's searchTitles), just
// sourced from that shared preference instead of from here.
const BOOL_KEYS = {
  unwatched: 'unwatched',
  hideWatching: 'hw',
  circleRatedHigh: 'friends',
  circleWatching: 'watching',
  releasedOnly: 'released',
  atHome: 'home',
  inTheaters: 'theaters',
};

/** 'theaters' | 'home' | '' — only ONE of the two boxes checked restricts; neither/both = no restriction. */
export function availabilityMode(f) {
  if (f.inTheaters && !f.atHome) return 'theaters';
  if (f.atHome && !f.inTheaters) return 'home';
  return '';
}

const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
/** Ids may be numeric (TMDB) or strings (demo); keep numbers as numbers. */
const parseId = (v) => (/^\d+$/.test(v) ? Number(v) : v);

export function filtersToSearchParams(filters, view) {
  const p = new URLSearchParams();
  for (const [field, key] of Object.entries(LIST_KEYS)) {
    if (filters[field]?.length) p.set(key, filters[field].join(','));
  }
  for (const [field, key] of Object.entries(SCALAR_KEYS)) {
    const v = filters[field];
    if (v !== null && v !== '' && v !== DEFAULT_FILTERS[field]) p.set(key, String(v));
  }
  // Always send minVotes unless explicitly cleared to "Any" (null) — the
  // default (25) must still reach the server, since "not sent" is what
  // means "no floor requested" server-side.
  if (filters.minVotes !== null) p.set('votes', String(filters.minVotes));
  // Always send sort explicitly: the backend's own fallback is "popular", so
  // omitting the (now "newest") default would return Popular results under a "Newest" label.
  p.set('sort', filters.sort);
  for (const [field, key] of Object.entries(BOOL_KEYS)) if (filters[field]) p.set(key, '1');
  if (view && view !== 'grid') p.set('view', view);
  return p;
}

export function searchParamsToState(search) {
  const p = new URLSearchParams(search);
  const f = { ...DEFAULT_FILTERS };
  for (const [field, key] of Object.entries(LIST_KEYS)) {
    const raw = p.get(key);
    f[field] = raw ? raw.split(',').filter(Boolean).map(parseId) : [];
  }
  f.q = p.get('q') ?? '';
  f.yearFrom = num(p.get('from'));
  f.yearTo = num(p.get('to'));
  f.ratingMin = num(p.get('rmin')) ?? RATING_MIN;
  f.ratingMax = num(p.get('rmax')) ?? RATING_MAX;
  f.minVotes = num(p.get('votes'));
  f.runtime = RUNTIME_OPTIONS.some((o) => o.id === p.get('len')) ? p.get('len') : 'any';
  f.language = /^[a-z]{2}$/.test(p.get('lang') ?? '') ? p.get('lang') : '';
  f.ageMax = AGE_OPTIONS.some((o) => o.id === p.get('age')) ? p.get('age') : 'any';
  f.sort = SORT_OPTIONS.some((o) => o.id === p.get('sort')) ? p.get('sort') : DEFAULT_FILTERS.sort;
  f.page = Math.max(1, num(p.get('page')) ?? 1);
  for (const [field, key] of Object.entries(BOOL_KEYS)) f[field] = p.get(key) === '1';
  const view = VIEWS.includes(p.get('view')) ? p.get('view') : 'grid';
  return { filters: f, view, hasAny: [...p.keys()].length > 0 };
}

/** Stable key for "did the query change?" — excludes nothing; view is NOT part of filters. */
export const queryKey = (filters) => JSON.stringify(filters);

/** Normalises a filters object for equality checks that shouldn't care about
 * incidental differences: array order (e.g. genres picked in a different
 * order), a stale `page`, or keys that didn't exist yet when an older saved
 * search was created (merged in from DEFAULT_FILTERS instead of comparing
 * as "missing" vs "present but empty"). Used by the toolbar's "is this saved
 * shortcut the one currently applied?" highlight — NOT by the actual
 * refetch-dedup check above, which intentionally stays byte-exact. */
export function canonicalFilters(f) {
  const merged = { ...DEFAULT_FILTERS, ...f, page: 1 };
  return {
    ...merged,
    types: [...merged.types].sort(),
    genres: [...merged.genres].sort((a, b) => a - b),
    providers: [...merged.providers].sort((a, b) => a - b),
  };
}

/* --------------------------------------------------------------- Labels -- */

// Whole numbers read as "7", not "7.0" — RATING_STEP is now 1 so the
// slider itself never produces anything else, but this still formats
// cleanly for any OLD saved search/URL left over from when it was 0.5
// (e.g. a stored ratingMin of 7.5 still renders as "7.5+", not rounded).
export const fmtRating = (n) => {
  const num = Number(n);
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
};

export function ratingLabel({ ratingMin, ratingMax }) {
  if (ratingMin <= RATING_MIN && ratingMax >= RATING_MAX) return null;
  if (ratingMax >= RATING_MAX) return `${fmtRating(ratingMin)}+`;
  if (ratingMin <= RATING_MIN) return `Up to ${fmtRating(ratingMax)}`;
  return `${fmtRating(ratingMin)}–${fmtRating(ratingMax)}`;
}

export function typeLabel(types) {
  if (!types.length) return 'Movies + TV';
  if (types.length === CONTENT_TYPES.length) return 'Movies + TV';
  return CONTENT_TYPES.find((t) => t.id === types[0])?.label ?? 'Movies + TV';
}

export function multiLabel(selectedIds, options, allLabel, pluralNoun) {
  if (!selectedIds.length) return allLabel;
  if (selectedIds.length === 1) return options.find((o) => o.id === selectedIds[0])?.name ?? `1 ${pluralNoun}`;
  return `${selectedIds.length} ${pluralNoun}`;
}

/** Services pill/summary text — reflects the shared "On my services" toggle
 * UNIONed with whatever individual tiles are tapped on top of it:
 * "All Services" (toggle off, nothing tapped), "My services" (toggle on,
 * nothing extra), "My services + Tubi" (toggle on, one extra service not
 * already covered by the toggle), "My services + 2" (toggle on, 2+ extra),
 * or the plain tile count/name when the toggle is off ("3 services"). */
export function servicesSummaryLabel({ enabled, selectedIds, userProviderIds = [], providers = [] }) {
  if (!enabled) return multiLabel(selectedIds, providers, 'All Services', 'services');
  const extra = selectedIds.filter((id) => !userProviderIds.includes(id));
  if (!extra.length) return 'My services';
  if (extra.length === 1) {
    const name = providers.find((p) => p.id === extra[0])?.name;
    return name ? `My services + ${name}` : 'My services + 1';
  }
  return `My services + ${extra.length}`;
}

/** Number of active non-default filters (for the mobile "Filters (n)" button). */
export function activeFilterCount(f) {
  let n = 0;
  if (f.types.length) n++;
  n += f.genres.length + f.providers.length;
  if (f.yearFrom || f.yearTo) n++;
  if (ratingLabel(f)) n++;
  for (const k of Object.keys(BOOL_KEYS)) if (f[k]) n++;
  if (f.minVotes !== DEFAULT_FILTERS.minVotes) n++;
  if (f.runtime !== 'any') n++;
  if (f.language) n++;
  if (f.ageMax !== 'any') n++;
  return n;
}

export function yearOptions() {
  const last = new Date().getFullYear() + 1;
  return Array.from({ length: last - 1900 + 1 }, (_, i) => last - i);
}

/** Default name offered when saving, e.g. "Comedy · Netflix · 2020–2026 · 7+". */
export function suggestSearchName(f, genres = [], providers = [], languages = DEFAULT_LANGUAGES) {
  const parts = [];
  if (f.q) parts.push(`“${f.q}”`);
  if (f.types.length === 1) parts.push(typeLabel(f.types));
  if (f.inTheaters && !f.atHome) parts.push('In theaters');
  if (f.atHome && !f.inTheaters) parts.push('Streaming at home');
  const nameOf = (list, id) => list.find((o) => o.id === id)?.name;
  if (f.genres.length) parts.push(f.genres.slice(0, 2).map((id) => nameOf(genres, id)).filter(Boolean).join(' + ') + (f.genres.length > 2 ? ' +' : ''));
  if (f.providers.length) parts.push(f.providers.slice(0, 2).map((id) => nameOf(providers, id)).filter(Boolean).join(' + ') + (f.providers.length > 2 ? ' +' : ''));
  if (f.yearFrom || f.yearTo) parts.push(`${f.yearFrom ?? 'Any'}–${f.yearTo ?? 'Now'}`);
  const rl = ratingLabel(f);
  if (rl) parts.push(rl);
  if (f.circleRatedHigh) parts.push('Loved by friends');
  if (f.circleWatching) parts.push('Friends watching now');
  if (f.unwatched) parts.push('Unwatched');
  if (f.hideWatching) parts.push('Not currently watching');
  if (f.runtime !== 'any') parts.push(RUNTIME_OPTIONS.find((o) => o.id === f.runtime)?.label);
  if (f.language) parts.push(languages.find((l) => l.id === f.language)?.name ?? f.language.toUpperCase());
  if (f.ageMax !== 'any') parts.push(`≤${f.ageMax}`);
  const name = parts.filter(Boolean).join(' · ');
  return (name || 'All titles').slice(0, 80);
}

/* ------------------------------------------------- services "showing" -- */

/**
 * The ONE rule for describing which streaming services are filtering
 * (DECISIONS #18). The "Currently Showing" chips, the Streaming Services
 * header summary, the Active Filters chip and the tile checkmarks are all
 * built from this, so they can never disagree.
 *
 *  - "On my services" on                  → base "My Services (8)"
 *  - toggle off, ALL own services picked  → base "My Services (8)" too
 *  - toggle off, 2+ (not all) own picked  → base "7 of 8 subscriptions"
 *  - toggle off, exactly 1 own picked     → that service's name
 *  - any non-subscription services        → listed by name after the base
 *
 * Returns { base, extras, label, subtitle, activeIds } where base is
 * null | { kind: 'mine'|'subset'|'single', label, ids } and extras is a list
 * of { id, name }. `activeIds` is every service id that is filtering.
 */
export function servicesShowing({ enabled, selectedIds = [], userProviderIds = [], providers = [] }) {
  const own = userProviderIds;
  const nameOf = (id) => providers.find((p) => p.id === id)?.name ?? 'Service';
  const ownPicked = enabled ? own : selectedIds.filter((id) => own.includes(id));
  const extras = selectedIds.filter((id) => !own.includes(id)).map((id) => ({ id, name: nameOf(id) }));
  let base = null;
  if (own.length && ownPicked.length === own.length && (enabled || own.length > 1)) {
    base = { kind: 'mine', label: `My Services (${own.length})`, ids: own };
  } else if (ownPicked.length >= 2) {
    base = { kind: 'subset', label: `${ownPicked.length} of ${own.length} subscriptions`, ids: ownPicked };
  } else if (ownPicked.length === 1) {
    base = { kind: 'single', label: nameOf(ownPicked[0]), ids: ownPicked };
  }
  const parts = [base?.label, ...extras.map((e) => e.name)].filter(Boolean);
  // Keep the "(n)" count on the "My Services" base label even when extras
  // follow it ("My Services (8) + Tubi") — only the plain "All services"
  // fallback below has no count to begin with.
  const label = parts.length ? parts.join(' + ') : 'All services';
  const extraNames = extras.map((e) => e.name);
  const joinNames = (list) => (list.length <= 2 ? list.join(' and ') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`);
  let subtitle;
  if (!base && !extras.length) subtitle = 'Titles on every streaming service.';
  else if (base?.kind === 'mine') subtitle = extras.length ? `Titles from your subscriptions, plus ${joinNames(extraNames)}.` : 'Titles from your subscriptions.';
  else if (base?.kind === 'subset') subtitle = `${base.label}${extras.length ? ` + ${extraNames.join(' + ')}` : ''}.`;
  else subtitle = `Titles available on ${joinNames([...(base ? [base.label] : []), ...extraNames])}.`;
  return { base, extras, label, subtitle, activeIds: Array.from(new Set([...ownPicked, ...extras.map((e) => e.id)])) };
}

/* ------------------------------------------------ section summaries -- */

const joinShort = (names, empty) => {
  const list = names.filter(Boolean);
  if (!list.length) return empty;
  return list.length <= 2 ? list.join(', ') : `${list.slice(0, 2).join(', ')} +${list.length - 2}`;
};

/** One-line summary shown on the right of each filter section header. */
export function sectionSummaries(f, { genres = [], languages = DEFAULT_LANGUAGES, services } = {}) {
  const mode = availabilityMode(f);
  const rl = ratingLabel(f);
  const votes = f.minVotes === null ? 'any # of ratings' : `${f.minVotes.toLocaleString()}+ ratings`;
  return {
    type: f.types.length === 1 ? CONTENT_TYPES.find((t) => t.id === f.types[0])?.label : 'Movies + TV Series',
    where: mode === 'theaters' ? 'In Theaters' : mode === 'home' ? 'Streaming at Home' : 'Anywhere',
    genres: joinShort(f.genres.map((id) => genres.find((g) => g.id === id)?.name), 'Any'),
    social: joinShort(CIRCLE_FILTERS.filter((c) => f[c.id]).map((c) => c.label), 'None'),
    services: mode === 'theaters' ? 'Not used' : services?.label ?? 'All services',
    year: f.yearFrom || f.yearTo ? `${f.yearFrom ?? 'Any'}–${f.yearTo ?? 'Now'}` : 'Any',
    rating: `${rl ?? 'Any'} (${votes})`,
    activity: joinShort([f.unwatched && 'Hide watched', f.hideWatching && 'Hide watching'], 'None'),
    advanced: joinShort(
      [
        f.runtime !== 'any' && RUNTIME_OPTIONS.find((o) => o.id === f.runtime)?.label,
        f.language && (languages.find((l) => l.id === f.language)?.name ?? f.language.toUpperCase()),
        f.ageMax !== 'any' && AGE_OPTIONS.find((o) => o.id === f.ageMax)?.label,
        f.releasedOnly && 'Released only',
      ],
      'Length, Language, Age Rating',
    ),
  };
}

/* --------------------------------------------------- active filters -- */

/**
 * Every NON-DEFAULT filter as a removable chip, for the panel's "Active
 * Filters (n)" bar (DECISIONS #18). The count is always chips.length, so it
 * can't drift from what's shown. Defaults that are never chips: Movies + TV,
 * Where to Watch unset, minimum 25 ratings. `actions` supplies the setters:
 * { patch, clearServices }.
 */
export function activeFilterChips(f, { genres = [], languages = DEFAULT_LANGUAGES, services, actions }) {
  const { patch, clearServices } = actions;
  const chips = [];
  if (f.types.length === 1) chips.push({ key: 'type', label: CONTENT_TYPES.find((t) => t.id === f.types[0])?.label, clear: () => patch({ types: [] }) });
  const mode = availabilityMode(f);
  if (mode === 'home') chips.push({ key: 'where', label: 'Streaming at Home', clear: () => patch({ atHome: false }) });
  if (mode === 'theaters') chips.push({ key: 'where', label: 'In Theaters', clear: () => patch({ inTheaters: false }) });
  for (const id of f.genres) {
    const name = genres.find((g) => g.id === id)?.name;
    if (name) chips.push({ key: `genre-${id}`, label: name, clear: () => patch({ genres: f.genres.filter((x) => x !== id) }) });
  }
  if (mode !== 'theaters' && services && (services.base || services.extras.length)) {
    chips.push({ key: 'services', label: services.label, clear: clearServices });
  }
  for (const c of CIRCLE_FILTERS) if (f[c.id]) chips.push({ key: c.id, label: c.label, clear: () => patch({ [c.id]: false }) });
  if (f.yearFrom || f.yearTo) chips.push({ key: 'year', label: `${f.yearFrom ?? 'Any'}–${f.yearTo ?? 'Now'}`, clear: () => patch({ yearFrom: null, yearTo: null }) });
  const rl = ratingLabel(f);
  if (rl) chips.push({ key: 'rating', label: rl, clear: () => patch({ ratingMin: RATING_MIN, ratingMax: RATING_MAX }) });
  if (f.minVotes !== DEFAULT_FILTERS.minVotes) {
    chips.push({
      key: 'votes',
      label: f.minVotes === null ? 'Any # of ratings' : `${f.minVotes.toLocaleString()}+ ratings`,
      clear: () => patch({ minVotes: DEFAULT_FILTERS.minVotes }),
    });
  }
  if (f.unwatched) chips.push({ key: 'unwatched', label: 'Hide watched', clear: () => patch({ unwatched: false }) });
  if (f.hideWatching) chips.push({ key: 'hideWatching', label: 'Hide watching', clear: () => patch({ hideWatching: false }) });
  if (f.runtime !== 'any') chips.push({ key: 'len', label: RUNTIME_OPTIONS.find((o) => o.id === f.runtime)?.label, clear: () => patch({ runtime: 'any' }) });
  if (f.language) chips.push({ key: 'lang', label: languages.find((l) => l.id === f.language)?.name ?? f.language.toUpperCase(), clear: () => patch({ language: '' }) });
  if (f.ageMax !== 'any') chips.push({ key: 'age', label: AGE_OPTIONS.find((o) => o.id === f.ageMax)?.label, clear: () => patch({ ageMax: 'any' }) });
  if (f.releasedOnly) chips.push({ key: 'released', label: 'Released only', clear: () => patch({ releasedOnly: false }) });
  return chips;
}
